import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getPinnedConversations } from '@/utils/pinnedConversations';

// Define a type for the shape of the data coming from the conversations_view
interface ConversationFromView {
  conversation_id: string;
  last_message_at: string;
  participants: {
    id: string;
    full_name: string;
    avatar_url: string;
  }[];
  last_message: {
    content: string;
    created_at: string;
    sender_id: string;
  } | null;
  participant_ids: string[];
}

export interface Conversation {
  id: string;
  participants: {
    id: string;
    name: string;
    avatar: string;
    isOnline: boolean;
    isTyping: boolean;
    lastSeen?: string;
  }[];
  lastMessage?: {
    id?: string;
    content: string;
    sentAt: string;
    isRead: boolean;
    senderId?: string;
    attachmentUrl?: string;
  };
  unreadCount?: number;
  isPinned?: boolean;
  isArchived?: boolean;
  isMuted?: boolean;
}

export const useConversations = (userId?: string) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const selectedConversationIdRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      
      // Get all conversation IDs for the user
      const { data: participantRows, error: participantsError } = await supabase
        .from('realtime_conversation_participants')
        .select('conversation_id')
        .eq('user_id', userId);
      
      if (participantsError) {
        throw participantsError;
      }
      
      if (!participantRows || participantRows.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }
      
      const conversationIds = participantRows.map((row) => row.conversation_id);
      
      // Fetch conversation metadata
      const { data: conversationsData, error: conversationsError } = await supabase
        .from('realtime_conversations')
        .select('id, created_at, updated_at, last_message_at, is_archived')
        .in('id', conversationIds)
        .order('last_message_at', { ascending: false });
        
      if (conversationsError) throw conversationsError;
      
      if (!conversationsData || conversationsData.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }
      
      // Fetch all participants
      const { data: allParticipants, error: allParticipantsError } = await supabase
        .from('realtime_conversation_participants')
        .select('conversation_id, user_id, joined_at, last_read_at, is_muted')
        .in('conversation_id', conversationIds);

      if (allParticipantsError) throw allParticipantsError;
      if (!allParticipants) {
          setConversations([]);
          setLoading(false);
          return;
      }

      // Get all unique user IDs from the participants
      const userIds = [...new Set(allParticipants.map(p => p.user_id))];

      // Fetch all profiles for those users in a single query
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .in('id', userIds);

      if (profilesError) throw profilesError;
      
      const profilesMap = new Map(profiles?.map(p => [p.id, p]));

      // Fetch online status for all users
      const { data: userStatuses } = await supabase
        .from('user_status')
        .select('user_id, last_seen')
        .in('user_id', userIds);
      
      const statusMap = new Map(userStatuses?.map(s => [s.user_id, s.last_seen]));

      // Manually join participants with their profiles
      const allParticipantsWithProfiles = allParticipants.map(p => ({
          ...p,
          profiles: profilesMap.get(p.user_id)
      }));

      // Fetch last message for each conversation
      const lastMessagesPromises = conversationIds.map(async (convId) => {
        // Find other participants for this conversation
        const otherParticipants = allParticipantsWithProfiles
          .filter(p => p.conversation_id === convId && p.user_id !== userId)
          .map(p => p.user_id);
          
        if (otherParticipants.length === 0) {
          return { conversationId: convId, lastMessage: null };
        }
        
        const { data: messages, error: messageError } = await supabase
          .from('real_messages')
          .select('id, content, created_at, profile_id, recipient_id, attachment_url')
          .eq('conversation_id', convId)
          .order('created_at', { ascending: false })
          .limit(1);
          
        if (messageError) {
          console.error('Error fetching last message for conversation', convId, messageError);
        }
          
        return { 
          conversationId: convId, 
          lastMessage: messages && messages.length > 0 ? messages[0] : null 
        };
      });
      
      const lastMessagesResults = await Promise.all(lastMessagesPromises);
      const lastMessagesMap: { [key: string]: (typeof lastMessagesResults[number])['lastMessage'] } = {};
      
      lastMessagesResults.forEach(result => {
        if (result.lastMessage) {
          lastMessagesMap[result.conversationId] = result.lastMessage;
        }
      });

      // Fetch unread counts in a single batch query
      const { data: unreadRows } = await supabase
        .from('real_messages')
        .select('conversation_id')
        .in('conversation_id', conversationIds)
        .neq('profile_id', userId)
        .eq('is_read', false);

      const unreadCountMap: Record<string, number> = {};
      (unreadRows || []).forEach(row => {
        unreadCountMap[row.conversation_id] = (unreadCountMap[row.conversation_id] || 0) + 1;
      });
      
      // Build conversation objects with all the data we've collected
      const formattedConversations: Conversation[] = conversationsData.map((conv) => {
        const allParticipants = allParticipantsWithProfiles.filter(p => p.conversation_id === conv.id);
        
        const participants = allParticipants
          .filter(p => p.user_id !== userId)
          .map(p => {
            // Safely check if profiles is a valid object and not an array or error
            const profileData = p.profiles && typeof p.profiles === 'object' && !Array.isArray(p.profiles) 
              ? (p.profiles as { full_name: string | null, avatar_url: string | null }) 
              : null;
            
            const fullName = profileData?.full_name || 'Unknown User';
            
            const avatarUrl = profileData?.avatar_url || '';
            
            const lastSeen = statusMap.get(p.user_id);
            // Consider online if last_seen within last 60 seconds
            const isOnline = lastSeen 
              ? (Date.now() - new Date(lastSeen).getTime()) < 60000 
              : false;
            
            return {
              id: p.user_id,
              name: fullName,
              avatar: avatarUrl,
              isOnline,
              isTyping: false,
              lastSeen: lastSeen || p.last_read_at
            };
          });
          
        const lastMessage = lastMessagesMap[conv.id];
        
        // Get pinned conversation IDs from localStorage
        const pinnedIds = getPinnedConversations(userId);
        
        // Get current user's participant record for is_muted
        const currentUserParticipant = allParticipants.find(p => p.user_id === userId && p.conversation_id === conv.id);
        
        return {
          id: conv.id,
          participants,
          unreadCount: unreadCountMap[conv.id] || 0,
          isPinned: pinnedIds.includes(conv.id),
          isArchived: conv.is_archived || false,
          isMuted: currentUserParticipant?.is_muted || false,
          lastMessage: lastMessage ? {
            id: lastMessage.id,
            content: lastMessage.content,
            sentAt: lastMessage.created_at,
            isRead: false,
            senderId: lastMessage.profile_id,
            attachmentUrl: lastMessage.attachment_url
          } : undefined
        };
      });
      
      // Sort conversations: pinned first, then by last message time (most recent first)
      const sortedConversations = formattedConversations.sort((a, b) => {
        // Pinned conversations come first
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        
        // Then sort by last message time
        const aTime = a.lastMessage?.sentAt || conversationsData.find(c => c.id === a.id)?.last_message_at || '0';
        const bTime = b.lastMessage?.sentAt || conversationsData.find(c => c.id === b.id)?.last_message_at || '0';
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
      
      setConversations(sortedConversations);
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error fetching conversations:', error.message);
        toast.error('Failed to load conversations');
      } else {
        console.error('An unknown error occurred:', error);
        toast.error('An unknown error occurred while fetching conversations.');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const selectConversation = useCallback((conversationId: string | null) => {
    setSelectedConversationId(conversationId);
    selectedConversationIdRef.current = conversationId;
  }, []);

  // Mark all messages in a conversation as read
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    if (!userId) return;
    
    // Optimistic update first — clears badge immediately
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId 
        ? { ...conv, unreadCount: 0 }
        : conv
    ));
    
    try {
      // Try both filters to handle any RLS variation
      const { data, error } = await supabase
        .from('real_messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .eq('recipient_id', userId)
        .eq('is_read', false)
        .select('id');
      
      console.log('[markAsRead]', { rows: data?.length ?? 0, error });
      
      // If recipient_id filter matched nothing, fall back to sender-based filter
      if (!error && (!data || data.length === 0)) {
        const { data: data2, error: error2 } = await supabase
          .from('real_messages')
          .update({ is_read: true })
          .eq('conversation_id', conversationId)
          .neq('profile_id', userId)
          .eq('is_read', false)
          .select('id');
        console.log('[markAsRead fallback]', { rows: data2?.length ?? 0, error: error2 });
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [userId]);

  const createNewConversation = useCallback(async (otherUserId: string): Promise<string | null> => {
    if (!userId) return null;

    // Prevent creating conversation with yourself
    if (userId === otherUserId) {
      toast.error('You cannot start a conversation with yourself');
      return null;
    }

    try {
      // Use Supabase function to create a conversation
      const { data, error } = await supabase
        .rpc('create_realtime_conversation', {
          p_other_user_id: otherUserId
        });
      
      if (error) {
        throw error;
      }
      
      const raw = data as unknown;
      const conversationId: string | null =
        typeof raw === 'string'
          ? raw
          : Array.isArray(raw) && raw.length > 0 && typeof (raw[0] as { id?: string }).id === 'string'
          ? (raw[0] as { id: string }).id
          : raw && typeof raw === 'object' && typeof (raw as { id?: string }).id === 'string'
          ? (raw as { id: string }).id
          : null;
      
      // Refresh conversations
      await fetchConversations();
      
      return conversationId;
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
      return null;
    }
  }, [userId, fetchConversations]);

  useEffect(() => {
    if (userId) {
      fetchConversations();
      
      // Set up realtime subscription for new conversations
      const channel = supabase
        .channel('realtime_conversation_changes')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'realtime_conversation_participants',
          filter: `user_id=eq.${userId}`
        }, () => {
          fetchConversations();
        })
        .subscribe();
      
      // Also listen for conversation updates (e.g., last_message_at changes)
      const updatesChannel = supabase
        .channel('realtime_conversation_updates')
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'realtime_conversations'
        }, (payload) => {
          const typedPayload = payload as unknown as { new?: { id: string; last_message_at?: string } };
          const newRow = typedPayload?.new;
          if (!newRow || !newRow.id) return;

          // Update only the affected conversation locally and resort; no full reload
          setConversations(prev => {
            const exists = prev.find(c => c.id === newRow.id);
            if (!exists) return prev;
            const updated = prev.map(c => c.id === newRow.id
              ? {
                  ...c,
                  lastMessage: {
                    id: c.lastMessage?.id,
                    content: c.lastMessage?.content || '',
                    sentAt: newRow.last_message_at || c.lastMessage?.sentAt || new Date().toISOString(),
                    isRead: c.lastMessage?.isRead ?? false,
                    senderId: c.lastMessage?.senderId,
                  }
                }
              : c
            );
            return updated.sort((a, b) => {
              const aTime = a.lastMessage?.sentAt || '0';
              const bTime = b.lastMessage?.sentAt || '0';
              return new Date(bTime).getTime() - new Date(aTime).getTime();
            });
          });
        })
        .subscribe();

      // Listen for new messages to update preview content and ordering instantly
      const messagesChannel = supabase
        .channel('realtime_messages_sidebar')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'real_messages'
        }, (payload) => {
          const typedPayload = payload as unknown as { new?: { conversation_id: string; content: string; created_at: string; profile_id: string } };
          const newMsg = typedPayload?.new;
          if (!newMsg) return;
          setConversations(prev => {
            const exists = prev.find(c => c.id === newMsg.conversation_id);
            if (!exists) return prev;
            const isFromOther = newMsg.profile_id !== userId;
            const isActiveConversation = selectedConversationIdRef.current === newMsg.conversation_id;
            const updated = prev.map(c => c.id === newMsg.conversation_id
              ? {
                  ...c,
                  // Don't increment if the user is currently viewing this conversation
                  unreadCount: isFromOther && !isActiveConversation ? (c.unreadCount || 0) + 1 : c.unreadCount,
                  lastMessage: {
                    id: c.lastMessage?.id,
                    content: newMsg.content,
                    sentAt: newMsg.created_at,
                    isRead: false,
                    senderId: newMsg.profile_id,
                  }
                }
              : c
            );
            return updated.sort((a, b) => {
              const aTime = a.lastMessage?.sentAt || '0';
              const bTime = b.lastMessage?.sentAt || '0';
              return new Date(bTime).getTime() - new Date(aTime).getTime();
            });
          });
        })
        .subscribe();

      // When messages are marked as read, reset unread count for that conversation
      const readChannel = supabase
        .channel('realtime_messages_read')
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'real_messages',
        }, (payload) => {
          const typedPayload = payload as unknown as { new?: { conversation_id: string; is_read: boolean; profile_id: string } };
          const newRow = typedPayload?.new;
          if (!newRow || !newRow.is_read) return;
          setConversations(prev =>
            prev.map(c =>
              c.id === newRow.conversation_id
                ? { ...c, unreadCount: 0 }
                : c
            )
          );
        })
        .subscribe();
        
      return () => {
        supabase.removeChannel(channel);
        supabase.removeChannel(updatesChannel);
        supabase.removeChannel(messagesChannel);
        supabase.removeChannel(readChannel);
      };
    }
  }, [userId, fetchConversations]);

  return {
    conversations,
    loading,
    selectedConversationId,
    selectConversation,
    fetchConversations,
    createNewConversation,
    markConversationAsRead
  };
};
